import React, { useState, useEffect } from 'react';
import {
  X, Download, Printer, Search, RefreshCw, FileText, Filter, Book, Clock, Calendar
} from 'lucide-react';
import apiClient from '../services/api';
import { useNavigate } from 'react-router-dom';

const NAVY       = '#0d1c6b';
const NAVY_LIGHT = '#1a3a8f';

const LedgerReport = ({ onClose }) => {
  const navigate = useNavigate();
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState('');
  const [reportData, setReportData] = useState(null);
  const [accounts,   setAccounts]   = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters,    setFilters]    = useState({
    AccountID: '',
    StartDate: new Date(new Date().getFullYear(), 0, 1).toISOString().slice(0, 10),
    EndDate:   new Date().toISOString().slice(0, 10),
  });

  function getSessionData() {
    try {
      const s = localStorage.getItem('ltms_session');
      if (!s) return null;
      const d = JSON.parse(s);
      return d?.session || d;
    } catch { return null; }
  }
  const session = getSessionData();

  useEffect(() => { loadAccounts(); }, []);

  const loadAccounts = async () => {
    try {
      const res = await apiClient.get('/AccountHeads.php');
      if (res.data.success) setAccounts(res.data.data || []);
    } catch (err) { console.error('Error loading accounts:', err); }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleGenerateReport = async () => {
    if (!filters.AccountID)               { setError('Please select an account');   return; }
    if (!filters.StartDate || !filters.EndDate) { setError('Please select date range'); return; }
    setLoading(true); setError('');
    try {
      const res = await apiClient.get('/LedgerReport.php', { params: filters });
      if (res.data.success) setReportData(res.data.data);
      else setError(res.data.message || 'Failed to generate report');
    } catch (err) { setError('Error generating report: ' + (err.response?.data?.message || err.message)); }
    finally { setLoading(false); }
  };

  const handlePrint = () => window.print();

  const handleExportCSV = () => {
    if (!reportData?.transactions?.length) { alert('No data to export'); return; }
    const headers = ['Date','Voucher No','Type','Particulars','Debit','Credit','Balance'];
    const rows    = reportData.transactions.map(t => [t.TransDate||'',t.VoucherNo||'',t.VoucherType||'',t.Particulars||'',t.Debit||'0.00',t.Credit||'0.00',t.Balance||'0.00']);
    const csv     = [headers.join(','),...rows.map(r=>r.map(c=>`"${c}"`).join(','))].join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv],{type:'text/csv;charset=utf-8;'}));
    a.download = `ledger_${reportData.account?.ACHeadCode}_${filters.StartDate}_to_${filters.EndDate}.csv`;
    a.style.visibility = 'hidden';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  const handleClearFilters = () => {
    setFilters({ AccountID:'', StartDate:new Date(new Date().getFullYear(),0,1).toISOString().slice(0,10), EndDate:new Date().toISOString().slice(0,10) });
    setReportData(null); setSearchTerm('');
  };

  const filteredAccounts = accounts.filter(acc =>
    acc.acheadname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    acc.acheadcode?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const inpCls = 'w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white outline-none transition-all duration-150 focus:border-blue-400 focus:shadow-[0_0_0_3px_rgba(59,130,246,0.1)] box-border font-[inherit]';
  const lblCls = 'block text-sm font-semibold text-gray-700 mb-1';

  // Dr/Cr badge
  const BalBadge = ({ type }) => (
    <span className={`inline-block px-2 py-px rounded text-[11px] font-bold ml-2 ${type==='Dr'?'bg-red-100 text-red-700':'bg-emerald-100 text-emerald-700'}`}>{type}</span>
  );

  // Voucher type badge color (same as DayBook)
  const voucherBadgeCls = (type) => {
    const t = (type||'').toLowerCase();
    if (t==='cv'||t==='cr') return 'bg-emerald-100 text-emerald-800';
    if (t==='bv'||t==='br') return 'bg-blue-100 text-blue-800';
    if (t==='jv')            return 'bg-violet-100 text-violet-800';
    return 'bg-gray-100 text-gray-700';
  };

  if (!session?.companyid) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-5 pb-4 border-b-2 border-gray-200">
          <div><h2 className="text-2xl font-bold text-gray-800 m-0">Session Required</h2><p className="text-sm text-gray-500 mt-1 m-0">Please select company and year</p></div>
        </div>
        <div className="text-center py-20 text-gray-500">
          <div className="text-5xl mb-4">🔒</div>
          <h3 className="font-bold text-gray-700 mb-2">Session Required</h3>
          <p className="text-sm">Please select a company and financial year from the dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-5 max-w-[1100px] mx-auto box-border">
      <style>{`
        @media print {
          .no-print { display:none !important; }
          .print-only { display:flex !important; }
        }
        .print-only { display:none; }
        @keyframes spin { to { transform:rotate(360deg); } }
        .lr-tr:hover td { background:#f9fafb; }
      `}</style>

      {/* Page header */}
      <div className="flex items-center justify-between mb-5 pb-4 border-b-2 border-gray-200">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 m-0">Ledger Report</h2>
          <p className="text-sm text-gray-500 mt-1 m-0">Account-wise Transaction Details</p>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex gap-2.5 flex-wrap mb-5 no-print">
        <button onClick={handleGenerateReport} disabled={loading}
          className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-white border-0 rounded-lg cursor-pointer transition-all duration-150 hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ background:`linear-gradient(135deg,${NAVY},${NAVY_LIGHT})` }}>
          <RefreshCw size={18}/> Generate Report
        </button>
        <button onClick={handlePrint} disabled={!reportData||loading}
          className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-gray-600 border-0 rounded-lg cursor-pointer hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150">
          <Printer size={18}/> Print
        </button>
        <button onClick={handleExportCSV} disabled={!reportData||loading}
          className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-emerald-600 border-0 rounded-lg cursor-pointer hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150">
          <Download size={18}/> Export CSV
        </button>
        <button onClick={handleClearFilters} disabled={loading}
          className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150">
          <RefreshCw size={18}/> Clear
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-300 text-red-700 rounded-lg mb-4 text-sm no-print">
          <span className="text-lg">⚠</span><div><strong>Error:</strong> {error}</div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 mb-5 no-print">
        <h3 className="flex items-center gap-2 text-[0.95rem] font-semibold text-gray-800 mb-4 pb-3 border-b-2 border-gray-100 m-0">
          <Filter size={18}/> Report Filters
        </h3>
        <div className="grid grid-cols-3 gap-4 max-[640px]:grid-cols-1">
          {/* Account select — spans full row */}
          <div className="col-span-3">
            <label className={lblCls}>Select Account <span className="text-red-500">*</span></label>
            <div className="flex gap-2 items-center mb-2">
              <input type="text" placeholder="Search accounts..."
                value={searchTerm} onChange={e=>setSearchTerm(e.target.value)}
                className={inpCls}/>
              <Search size={18} className="text-gray-400 flex-shrink-0"/>
            </div>
            <select name="AccountID" value={filters.AccountID} onChange={handleFilterChange}
              required size={6} className={`${inpCls} cursor-pointer min-h-[130px]`}>
              <option value="">-- Select Account --</option>
              {filteredAccounts.map(acc=>(
                <option key={acc.acheadid} value={acc.acheadid}>
                  {acc.acheadcode} - {acc.acheadname} ({acc.acgroupname})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={lblCls}>Start Date <span className="text-red-500">*</span></label>
            <input type="date" name="StartDate" value={filters.StartDate} onChange={handleFilterChange} required className={inpCls}/>
          </div>
          <div>
            <label className={lblCls}>End Date <span className="text-red-500">*</span></label>
            <input type="date" name="EndDate" value={filters.EndDate} onChange={handleFilterChange} required className={inpCls}/>
          </div>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex flex-col items-center gap-3 py-16 text-gray-500">
          <span className="w-10 h-10 border-4 border-gray-200 border-t-blue-500 rounded-full block" style={{ animation:'spin 0.8s linear infinite' }}/>
          <p className="m-0 text-sm">Generating ledger report...</p>
        </div>
      )}

      {/* Report */}
      {reportData && !loading && (
        <div>
          {/* Report title */}
          <div className="text-center mb-6 pb-5 border-b-2 border-gray-200">
            <h1 className="text-2xl font-extrabold text-gray-900 m-0 mb-1">{reportData.company?.CompanyName||'Company Name'}</h1>
            <h2 className="text-lg font-bold text-gray-600 m-0 mb-3">Ledger Report</h2>
            <div className="flex items-center justify-center gap-6 flex-wrap text-sm text-gray-500">
              <span className="flex items-center gap-1.5"><Calendar size={16}/>Period: {new Date(filters.StartDate).toLocaleDateString()} to {new Date(filters.EndDate).toLocaleDateString()}</span>
              <span className="flex items-center gap-1.5"><Clock size={16}/>Generated: {new Date().toLocaleString()}</span>
            </div>
          </div>

          {/* Account header */}
          <div className="bg-white border border-gray-200 rounded-xl p-5 mb-5 shadow-sm" style={{ background:`linear-gradient(135deg,${NAVY}18,transparent)`, borderLeft:`4px solid ${NAVY}` }}>
            <h2 className="text-xl font-bold text-gray-900 m-0 mb-1">{reportData.account?.ACHeadName}</h2>
            <div className="font-mono text-sm text-gray-500 mb-2">{reportData.account?.ACHeadCode}</div>
            <div className="text-sm text-gray-500 font-semibold">Group: {reportData.account?.GroupName}</div>
          </div>

          {/* Opening balance card */}
          <div className="grid grid-cols-1 gap-4 mb-6">
            <div className={`rounded-xl p-5 text-center border ${reportData.openingBalance?.Type==='Dr'?'bg-emerald-50 border-emerald-200':'bg-red-50 border-red-200'}`}>
              <div className="text-xs font-bold uppercase tracking-wide mb-1" style={{ color:reportData.openingBalance?.Type==='Dr'?'#059669':'#dc2626' }}>Opening Balance</div>
              <div className="text-2xl font-extrabold font-mono" style={{ color:reportData.openingBalance?.Type==='Dr'?'#065f46':'#991b1b' }}>
                {parseFloat(reportData.openingBalance?.Amount||0).toFixed(2)} {reportData.openingBalance?.Type||'Dr'}
              </div>
            </div>
          </div>

          {/* Transactions table */}
          {reportData.transactions?.length > 0 ? (
            <div className="overflow-x-auto border border-gray-200 rounded-xl shadow-sm mb-6">
              <table className="w-full border-collapse text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {[['Date','w-[100px]'],['Voucher No','w-[120px]'],['Type','w-[60px]'],['Particulars',''],['Debit','w-[120px] text-right'],['Credit','w-[120px] text-right'],['Balance','w-[140px] text-right']].map(([h,cls])=>(
                      <th key={h} className={`px-4 py-3 text-left font-semibold text-gray-600 text-xs uppercase tracking-wide border-b border-gray-200 ${cls}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* Opening balance row */}
                  <tr className="bg-gray-50 font-bold border-b border-gray-200">
                    <td colSpan={4} className="px-4 py-2.5 uppercase text-gray-600 text-xs tracking-wide">Opening Balance</td>
                    <td className="px-4 py-2.5 text-right font-mono text-red-600">
                      {reportData.openingBalance?.Type==='Dr'?parseFloat(reportData.openingBalance.Amount||0).toFixed(2):'-'}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-emerald-700">
                      {reportData.openingBalance?.Type==='Cr'?parseFloat(reportData.openingBalance.Amount||0).toFixed(2):'-'}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono font-bold">
                      {parseFloat(reportData.openingBalance?.Amount||0).toFixed(2)}<BalBadge type={reportData.openingBalance?.Type}/>
                    </td>
                  </tr>
                  {reportData.transactions.map((trans, index) => (
                    <tr key={index} className="lr-tr border-b border-gray-50 last:border-b-0">
                      <td className="px-4 py-2.5 text-[13px] text-gray-500">{new Date(trans.TransDate).toLocaleDateString()}</td>
                      <td className="px-4 py-2.5"><span className="font-mono text-[12px] text-gray-600">{trans.VoucherNo}</span></td>
                      <td className="px-4 py-2.5">
                        <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-bold ${voucherBadgeCls(trans.VoucherType)}`}>{trans.VoucherType}</span>
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="font-medium text-gray-800">{trans.Particulars}</div>
                        {trans.Remarks&&<div className="text-[12px] text-gray-400 mt-0.5 italic">{trans.Remarks}</div>}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        {parseFloat(trans.Debit||0)>0
                          ?<span className="font-bold text-red-600 font-mono">{parseFloat(trans.Debit).toFixed(2)}</span>
                          :<span className="text-gray-300">-</span>}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        {parseFloat(trans.Credit||0)>0
                          ?<span className="font-bold text-emerald-700 font-mono">{parseFloat(trans.Credit).toFixed(2)}</span>
                          :<span className="text-gray-300">-</span>}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono font-bold">
                        {parseFloat(trans.Balance||0).toFixed(2)}<BalBadge type={trans.BalanceType}/>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-50 font-bold border-t-2 border-gray-200">
                    <td colSpan={4} className="px-4 py-2.5 text-gray-600 font-semibold">Totals:</td>
                    <td className="px-4 py-2.5 text-right font-mono text-red-600">{parseFloat(reportData.totals?.TotalDebit||0).toFixed(2)}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-emerald-700">{parseFloat(reportData.totals?.TotalCredit||0).toFixed(2)}</td>
                    <td/>
                  </tr>
                  <tr className="bg-gray-50 font-bold border-t border-gray-200">
                    <td colSpan={6} className="px-4 py-2.5 text-gray-600 font-semibold">Closing Balance:</td>
                    <td className="px-4 py-2.5 text-right font-mono text-[18px]">
                      {parseFloat(reportData.closingBalance?.Amount||0).toFixed(2)}<BalBadge type={reportData.closingBalance?.Type}/>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div className="text-center py-16 text-gray-500 mb-6">
              <div className="text-5xl mb-4">📖</div>
              <h3 className="font-bold text-gray-700 mb-2">No Transactions Found</h3>
              <p className="text-sm">No transactions found for the selected period</p>
            </div>
          )}

          {/* Summary cards */}
          {reportData.totals && (
            <div className="grid grid-cols-3 gap-4 mt-6 max-[640px]:grid-cols-1">
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 text-center">
                <div className="text-xs font-bold uppercase tracking-wide text-emerald-600 mb-1">Total Debit</div>
                <div className="text-2xl font-extrabold text-emerald-800 font-mono">{parseFloat(reportData.totals.TotalDebit||0).toFixed(2)}</div>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-xl p-5 text-center">
                <div className="text-xs font-bold uppercase tracking-wide text-red-600 mb-1">Total Credit</div>
                <div className="text-2xl font-extrabold text-red-700 font-mono">{parseFloat(reportData.totals.TotalCredit||0).toFixed(2)}</div>
              </div>
              <div className={`${reportData.closingBalance?.Type==='Dr'?'bg-emerald-50 border-emerald-200':'bg-red-50 border-red-200'} border rounded-xl p-5 text-center`}>
                <div className="text-xs font-bold uppercase tracking-wide mb-1" style={{ color:reportData.closingBalance?.Type==='Dr'?'#059669':'#dc2626' }}>Closing Balance</div>
                <div className="text-2xl font-extrabold font-mono" style={{ color:reportData.closingBalance?.Type==='Dr'?'#065f46':'#991b1b' }}>
                  {parseFloat(reportData.closingBalance?.Amount||0).toFixed(2)} {reportData.closingBalance?.Type}
                </div>
              </div>
            </div>
          )}

          {/* Signature (print only) */}
          <div className="print-only justify-between mt-12 pt-6 border-t-2 border-gray-300">
            {['Prepared By','Checked By','Approved By'].map(label=>(
              <div key={label} className="text-center" style={{ minWidth:160 }}>
                <div className="border-t border-gray-500 pt-1 text-sm text-gray-600">{label}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LedgerReport;